import { FeatureToggles } from '@/features/feature-toggles'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type Document } from '@/features/documents/Document'
import { action, computed, observable } from 'mobx'
import { TabController } from '@/infra/TabController'
import { func, required, validationContext } from 'validx'
import { task } from 'mobx-task'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import type { GetCurrentMemberFn } from '@/features/iam'
import type { FetchJobFn } from '@/features/jobs'
import { type DocumentStore } from '@/features/documents/DocumentStore'
import { type DocumentTypeStore } from '@/features/documents/DocumentTypeStore'
import { NotFound } from 'fejl'
import { type DocumentType } from '@/features/documents/DocumentType'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { PromptDeleteDocumentFn } from '@/features/documents'

/**
 * View model for viewing and editing a document's details.
 */
export class DocumentDetailsModalViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  readonly modal = new ModalViewModel()

  @observable accessor title = ''
  @observable accessor description = ''
  @observable accessor documentType: DocumentType | null = null

  readonly descriptionLengthLimit = 200

  readonly validation = validationContext(this, {
    title: [
      required('Title is required'),
      func(
        (opts) => (opts.value as string).length >= 2,
        'Must be more than 2 characters.',
      ),
    ],
  })

  @observable accessor viewAsAdmin = false

  readonly tabController = new TabController<DocumentDetailsTab>(() => [
    {
      tab: 'Details',
      title: 'Details',
      hasError: () => false,
    },
  ])

  constructor(
    readonly document: Document,
    private readonly _documentStore: DocumentStore,
    private readonly _documentTypeStore: DocumentTypeStore,
    private readonly _fetchJob: FetchJobFn,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _promptDelete: PromptDeleteDocumentFn,
    private readonly _viewAsAdmin?: boolean,
  ) {
    this.title = this.document.title
    this.description = this.document.description
    this.documentType = this.document.documentType
    this.viewAsAdmin = this._viewAsAdmin ?? false
  }

  @computed
  get isDetailsTab() {
    return this.tabController.current.tab === 'Details'
  }

  /**
   * Whether loading, saving or deleting is underway.
   */
  @computed
  get isPending() {
    return this.fetch.pending || this.save.pending || this.delete.pending
  }

  /**
   * The listed type matching the selected document type.
   */
  @computed
  get selectedDocumentType() {
    return this.documentTypes.find((t) => t.id === this.documentType?.id)
  }

  @computed
  get hasChanges() {
    return (
      (this.canEditTitleAndDescription &&
        (this.document.title !== this.title ||
          this.document.description !== this.description)) ||
      this.document.documentType !== this.documentType
    )
  }

  /**
   * The v5 API has no endpoint to update the title and description,
   * so those fields are hidden while it's enabled.
   */
  @computed
  get canEditTitleAndDescription() {
    return !FeatureToggles.documentsApiV5()
  }

  @computed
  get documentTypes() {
    return [...this._documentTypeStore.types.items]
  }

  @computed
  get canChangeDocumentType() {
    return !this.document.documentType?.id || this.viewAsAdmin
  }

  @computed
  get canEdit() {
    return this.document.canEdit
  }

  @computed
  get headerDescription() {
    if (this.description !== '') {
      if (this.document.description !== this.description) {
        return this.description
      }
      return this.document.description
    }

    return ''
  }

  @computed
  get descriptionCharsRemaining() {
    if (this.description) {
      return this.descriptionLengthLimit - this.description?.length
    }
    return this.descriptionLengthLimit
  }

  @computed
  get headerTitle() {
    if (this.title !== '') {
      if (this.document.title !== this.title) {
        return this.title
      }

      return this.document.title
    }

    return ''
  }

  @computed
  get backingFileName() {
    return this.document.title
  }

  @computed
  get documentWorkspace() {
    return this.document.workspace
  }

  @computed
  get canDownload() {
    return this.document.downloadable
  }

  @computed
  get canRemove() {
    const { permissions } = this._getCurrentMember()
    return (
      permissions.docsSelfRemove ||
      permissions.docsClientRemove ||
      permissions.docsProviderRemove
    )
  }

  activate() {
    this._lifecycle.activate()
    // The form stays usable without the types or linked jobs, so a failed load is not surfaced.
    void this.fetch(this._lifecycle.signal).catch(() => undefined)
  }

  deactivate() {
    this._lifecycle.deactivate()
  }

  @action.bound
  show() {
    this.modal.show()
  }

  @action.bound
  close() {
    this.discardChanges()
    this.modal.close()
  }

  @action.bound
  discardChanges() {
    this.title = this.document.title
    this.description = this.document.description
    this.documentType = this.document.documentType
    this.validation.reset()
  }

  @action.bound
  selectDetailsTab() {
    this.tabController.change('Details')
  }

  @action.bound
  setViewAsAdmin(viewAsAdmin: boolean) {
    this.viewAsAdmin = viewAsAdmin
  }

  @action.bound
  setDocumentType(type: DocumentType | null | undefined) {
    if (this.canChangeDocumentType) {
      this.documentType = type ?? null
    }
  }

  @action.bound
  async downloadLatest() {
    if (this.canDownload) {
      await this._documentStore
        .download({ document: this.document, preview: false })
        .catch(this._createAlertErrorHandler('Downloading failed'))
    }
  }

  save = task.resolved(async () => {
    const valid = this.validation.reset().validate().isValid
    if (!valid) {
      return
    }

    try {
      if (
        this.documentType?.id &&
        this.canChangeDocumentType &&
        this.document.documentType !== this.documentType
      ) {
        await this._documentStore.assignType(this.document.id, {
          documentTypeId: this.documentType.id,
        })
      }

      if (this.canEditTitleAndDescription) {
        await this._documentStore.updateDocument(this.document.id, {
          title: this.title,
          description: this.description,
        })
      }
    } catch (err) {
      this._createAlertErrorHandler('Updating the document failed')(err)
    }

    this._newAlert()
      .withState('success')
      .saying(`Success! Document ${this.document.title} has been updated!`)
      .autoDismiss()
      .show()
    this.close()
  })

  delete = task.resolved(async () => {
    if (
      await this._promptDelete({
        document: this.document,
        signal: this._lifecycle.signal,
      })
    ) {
      this.close()
    }
  })

  /**
   * Loads the document types and the jobs the document is linked to.
   */
  fetch = task(async (signal: AbortSignal) => {
    await this._documentTypeStore.listTypes(signal)
    // The links on the document imply permission to read the jobs.
    await Promise.all(
      this.document.links.job.jobIds.map((jobId) =>
        this._fetchJob(jobId).catch(NotFound.ignore(null)),
      ),
    )
  })
}

export type DocumentDetailsTab = 'Details'

/**
 * Creates the details modal for a document.
 */
export type DocumentDetailsModalViewModelFactory = (
  document: Document,
  viewAsAdmin?: boolean,
) => DocumentDetailsModalViewModel
